import { useState, useEffect } from "react";
import { Phone, Menu, X, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Everyday Care", href: "#scope" },
  { label: "Chronic Care", href: "#chronic-care" },
  { label: "DermaGen", href: "#dermagen" },
  { label: "News", href: "#news" },
  { label: "Events", href: "#events" },
  { label: "Book", href: "#book" },
  { label: "Visit", href: "#contact" },
];

const openStaff = () => window.dispatchEvent(new CustomEvent("open-staff"));

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b backdrop-blur-xl transition-[background-color,box-shadow] duration-300 ${
        scrolled ? "bg-white/85 border-border/60 shadow-[0_4px_24px_rgba(0,0,0,0.05)]" : "bg-white/60 border-white/20"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 lg:px-10">
        <a href="#top" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src="/assets/priceline-logo-trimmed.png" alt="Priceline Pharmacy" className="h-12 w-auto" data-testid="header-logo" />
          <div className="hidden sm:block leading-tight">
            <p className="font-heading text-sm font-bold tracking-tight">Margaret River</p>
            <p className="text-xs text-muted-foreground">Open 7 days, 8am–6pm</p>
          </div>
        </a>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="header-nav">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}-link`}
              className="text-sm font-medium text-foreground/80 transition-colors duration-200 hover:text-brand-pink"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={openStaff}
            data-testid="header-staff-btn"
            className="hidden items-center gap-1.5 rounded-full border border-foreground/15 px-4 py-2 text-sm font-semibold text-foreground/70 transition-colors duration-200 hover:border-brand-pink hover:text-brand-pink md:flex"
          >
            <Lock className="h-3.5 w-3.5" /> Staff
          </button>
          <a
            href="tel:+61897572224"
            data-testid="header-call-link"
            className="hidden items-center gap-2 text-sm font-semibold text-brand-pink transition-colors duration-200 hover:text-brand-pinkdark md:flex"
          >
            <Phone className="h-4 w-4" /> 9757 2224
          </a>
          <Button
            asChild
            data-testid="header-book-now-btn"
            className="rounded-full bg-brand-pink px-5 font-heading font-semibold text-white hover:bg-brand-pinkdark"
          >
            <a href="#book">Book Now</a>
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            className="rounded-full p-2 hover:bg-muted lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-border/60 bg-white/95 px-5 py-4 lg:hidden" data-testid="mobile-nav">
          <div className="flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}-link`}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-muted"
              >
                {n.label}
              </a>
            ))}
            <button
              data-testid="mobile-staff-btn"
              onClick={() => { setOpen(false); openStaff(); }}
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-foreground/70"
            >
              <Lock className="h-4 w-4" /> Staff login
            </button>
            <a href="tel:+61897572224" data-testid="mobile-call-link" className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-brand-pink">
              <Phone className="h-4 w-4" /> Call 9757 2224
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
